import { Component, HostBinding, inject, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { RealisationHourtype } from '../../../../classes/realisation';
import { User } from '../../../../classes/user.class';
import { LeaveService } from '../../../../services/leave.service';
import { UserService } from '../../../../services/user/user.service';
import { Utils } from '../../../../utils.class';
import { InputComponent } from '../input/input.component';

@Component({
    selector: 'app-user-with-leave-input',
    standalone: false,
    templateUrl: './user-with-leave-input.component.html',
    styleUrl: './user-with-leave-input.component.scss',
})
export class UserWithLeaveInputComponent extends InputComponent implements OnInit {
    isRequired = false;
    @HostBinding('class.deleted') isDeleted = false;

    private userService = inject(UserService);
    private leaveService = inject(LeaveService);

    private users$ = combineLatest([
        this.userService.getByType(this.field.dataFilter as any[]),
        this.userService.getList(true),
    ]).pipe(
        map(([filteredUsers, allUsers]) => {
            const currentId = this.fc.value;

            if (currentId && !filteredUsers.find(u => u.id === currentId)) {
                const currentUser = allUsers.find(u => u.id === currentId);
                if (currentUser) {
                    return [...filteredUsers, currentUser];
                }
            }
            this.users = filteredUsers;
            return filteredUsers;
        })
    );

    employeesWithLeave: (User & {
        leaves?: { id: number; hourtype: RealisationHourtype; begintime: Date; endtime: Date }[];
    })[];
    usersWithLeave: Record<User['id'], boolean> = {};
    users: User[];

    ngOnInit() {
        this.subscriptions.add(
            combineLatest([this.fc.valueChanges.pipe(startWith(this.fc.value)), this.users$]).subscribe(
                ([userId, users]) => {
                    const user = users.find(u => u.id === userId);
                    this.isDeleted = !!user?.deleted_at;
                }
            )
        );

        const fcParent = this.fc.parent;
        const fcMainPhe = this.formData?.mainPhe?.form?.controls?.phe;
        const beginDate = fcParent?.get('begindate') ?? fcMainPhe?.get('begindate');
        const endDate = fcParent?.get('enddate') ?? fcMainPhe?.get('enddate');

        if (!beginDate || !endDate) {
            console.error(
                'UserWithLeaveInputComponent requires begindate and enddate form controls in the parent form group',
                {
                    field: this.field,
                    formData: this.formData,
                    fcParent,
                    fcMainPhe,
                }
            );
            return;
        }

        this.subscriptions.add(
            this.fc.statusChanges.subscribe(() => {
                this.isRequired = this.fc.hasValidator(Validators.required);
            })
        );

        const plannedDatesChanged = combineLatest([beginDate?.valueChanges, endDate?.valueChanges]).pipe(
            distinctUntilChanged(
                (prev, curr) =>
                    Utils.getTimeOrNull(new Date(prev[0])) === Utils.getTimeOrNull(new Date(curr[0])) &&
                    Utils.getTimeOrNull(new Date(prev[1])) === Utils.getTimeOrNull(new Date(curr[1]))
            ),
            debounceTime(50),
            startWith([beginDate?.value, endDate?.value])
        );

        this.subscriptions.add(
            plannedDatesChanged
                .pipe(
                    switchMap(() =>
                        this.leaveService.getFilteredList(new Date(beginDate.value), new Date(endDate.value))
                    )
                )
                .subscribe(leaves => {
                    this.employeesWithLeave = this.users?.map(e => ({
                        ...e,
                        leaves: this.leaveService
                            .dayLeaves(leaves ?? [], this.users ?? [], beginDate?.value)
                            .filter(l => l.users.map(u => u.id).includes(e.id))
                            .map(l => l.leave)
                            .map(l => ({
                                id: l.id,
                                hourtype: l.hourtype,
                                begintime: Utils.setTimeByString(new Date(l.begindate), l.begintime),
                                endtime: Utils.setTimeByString(new Date(l.enddate), l.endtime),
                            })),
                    }));
                    this.employeesWithLeave.forEach(e => (this.usersWithLeave[e.id] = e.leaves?.length > 0));
                })
        );
    }
}
